import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Eye, X } from "lucide-react";
import { PageLoader } from "@/components/ui/Spinner";
import { ContractorPreviewProvider } from "../contractorPreview";
import { useOrderAccess } from "../orderAccess";
import { useContractorOrderPreview } from "../hooks/useOrders";
import { ContractorOrderDetailPage } from "../pages/ContractorOrderDetailPage";

interface Props {
  orderId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Modal „Widok podwykonawcy" na stronie zamówienia (GK): renderuje ten sam komponent,
 * który widzi podwykonawca, na danych z endpointu contractor-preview (ukryte
 * przedsięwzięcia, stawki wg grantów kont firmy). Wszystkie akcje są wyłączone,
 * a nawigacja wewnętrzna zablokowana — pliki, tel: i mailto: działają normalnie.
 */
export function ContractorViewModal({ orderId, open, onClose }: Props) {
  // Oglądający bez kwot na własnej karcie (dostęp „Podgląd"): backend maskuje stawki w
  // podglądzie niezależnie od grantów kont podwykonawcy — przełącznik nie ma tu sensu.
  const { amountsHidden } = useOrderAccess();
  // undefined = wariant stawek wg faktycznych grantów kont podwykonawcy.
  const [ratesOverride, setRatesOverride] = useState<"visible" | "hidden" | undefined>(undefined);
  const { data: preview } = useContractorOrderPreview(orderId, open, amountsHidden ? undefined : ratesOverride);
  const meta = preview?.meta;
  const canSeeRates = amountsHidden
    ? false
    : ratesOverride ? ratesOverride === "visible" : meta?.can_see_rates ?? false;

  useEffect(() => {
    if (!open) {
      setRatesOverride(undefined);
      return;
    }
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const guardLinks = (e: React.MouseEvent) => {
    const anchor = (e.target as HTMLElement).closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href") ?? "";
    if (anchor.target === "_blank" || href.startsWith("tel:") || href.startsWith("mailto:")) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const effectiveVariant = ratesOverride ?? (meta?.can_see_rates ? "visible" : "hidden");

  return createPortal(
    <div data-modal-layer className="fixed inset-0 z-50 flex p-4 bg-black/50" onClick={onClose}>
      <div
        className="m-auto w-full max-w-[1400px] h-full max-h-[92vh] flex flex-col bg-[var(--color-bg-surface)] border border-[var(--color-border)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-3 min-w-0">
            <Eye size={16} className="shrink-0 text-[var(--color-primary)]" />
            <div className="min-w-0">
              <h2 className="text-sm font-mono font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
                Widok podwykonawcy
              </h2>
              <p className="text-xs text-[var(--color-text-muted)] truncate">
                Tak widzi to zamówienie {meta?.contractor_name ?? "podwykonawca"} — akcje są wyłączone.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {meta && amountsHidden && (
              <span className="text-xs text-[var(--color-text-muted)]" title="Twój dostęp do tego zamówienia nie obejmuje kwot">
                Stawki ukryte
              </span>
            )}
            {meta && !amountsHidden && (
              <div className="flex items-center gap-1.5 text-xs">
                <span
                  className="text-[var(--color-text-muted)] mr-0.5"
                  title={`${meta.accounts_with_rates} z ${meta.accounts_total} aktywnych kont podwykonawcy ma dostęp do stawek`}
                >
                  Stawki ({meta.accounts_with_rates}/{meta.accounts_total} kont):
                </span>
                {(["visible", "hidden"] as const).map((variant) => (
                  <button
                    key={variant}
                    onClick={() => setRatesOverride(variant)}
                    className={`px-2 py-0.5 rounded border transition-colors duration-100 ${
                      effectiveVariant === variant
                        ? "border-[var(--color-primary)] text-[var(--color-primary)] font-medium"
                        : "border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                    }`}
                  >
                    {variant === "visible" ? "widoczne" : "ukryte"}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={onClose}
              className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors duration-100"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-6" onClickCapture={guardLinks}>
          {preview ? (
            <ContractorPreviewProvider value={{ isPreview: true, canSeeRates, ratesOverride }}>
              <ContractorOrderDetailPage previewOrderId={orderId} />
            </ContractorPreviewProvider>
          ) : (
            <PageLoader />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
